import { useEffect, useRef, useState } from "react";

const SCRUB_THRESHOLD_PX = 8; // less movement than this is a normal tap

// Where the phone tab bar's center sits, as a fraction of the visible screen
// height measured up from the bottom (~18%: 1/7 + 4%). At 1/7 + 10% it read
// as floating mid-screen; 1/7 alone sat too close to the home indicator.
const TAB_BAR_CENTER_FRACTION = 1 / 7 + 0.04;
const TAB_BAR_HALF_HEIGHT = 26;

/**
 * Keeps --oc-tabbar-offset (px) in sync with the *visible* viewport height.
 * CSS vh on iOS Safari is the height with the toolbars hidden, so a vh-based
 * offset put the bar in a different spot on every device and toolbar state;
 * window.innerHeight is what's actually on screen (and, unlike
 * visualViewport, doesn't shrink when the keyboard opens).
 */
export function useTabBarOffset() {
  useEffect(() => {
    const root = document.documentElement;
    const update = () => {
      const h = window.innerHeight || root.clientHeight;
      root.style.setProperty("--oc-tabbar-offset", `${Math.round(h * TAB_BAR_CENTER_FRACTION - TAB_BAR_HALF_HEIGHT)}px`);

      // Content area = at least one visible screen, so a short cart never
      // pulls the footer up into view. Inside the iOS app the site runs in a
      // frame that can grow with its content (innerHeight would feed back
      // into itself), so the device screen height stands in there.
      let framed = false;
      try { framed = window.self !== window.top; } catch (_) { framed = true; }
      const screenH = framed ? (window.screen && window.screen.height) || h : h;
      const menu = document.querySelector(".main-content > .menu");
      const menuInFlow = menu && window.getComputedStyle(menu).position !== "fixed" ? menu.offsetHeight : 0;
      root.style.setProperty("--oc-main-min", `${Math.max(0, Math.round(screenH - menuInFlow))}px`);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
}

/**
 * Hold-and-slide across the phone tab bar (like the iOS tab bar): a glass
 * lens follows the finger, the tab under it lights up, and releasing opens
 * that tab. A plain tap is left alone, so the icons' own onClick still runs.
 *
 * Returns a ref for the bar element and the lens state to render
 * ({ x, active } or null).
 */
export default function useTabScrub() {
  const barRef = useRef(null);
  const stateRef = useRef(null); // { startX, scrubbing, item }
  const [lens, setLens] = useState(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;

    const itemAt = (x, y) => {
      const el = document.elementFromPoint(x, y);
      const item = el && el.closest(".menu-item");
      return item && bar.contains(item) ? item : null;
    };

    const mark = (item) => {
      bar.querySelectorAll(".menu-item.is-scrub").forEach((el) => el !== item && el.classList.remove("is-scrub"));
      if (item) item.classList.add("is-scrub");
    };

    const onStart = (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      stateRef.current = { startX: t.clientX, scrubbing: false, item: null };
    };

    const onMove = (e) => {
      const s = stateRef.current;
      if (!s) return;
      const t = e.touches[0];
      if (!s.scrubbing && Math.abs(t.clientX - s.startX) < SCRUB_THRESHOLD_PX) return;
      s.scrubbing = true;
      if (e.cancelable) e.preventDefault(); // the bar, not the page, owns this drag

      const barRect = bar.getBoundingClientRect();
      const y = barRect.top + barRect.height / 2;
      const item = itemAt(Math.min(Math.max(t.clientX, barRect.left + 1), barRect.right - 1), y);
      if (item !== s.item) {
        s.item = item;
        mark(item);
        if (item && navigator.vibrate) navigator.vibrate(5);
      }
      setLens({ x: t.clientX - barRect.left, active: !!item });
    };

    const onEnd = (e) => {
      const s = stateRef.current;
      stateRef.current = null;
      if (!s || !s.scrubbing) return;
      if (e.cancelable) e.preventDefault(); // swallow the synthetic click on the start icon
      mark(null);
      setLens(null);
      // The tab icons carry their own onClick (on the svg) — trigger it.
      const target = s.item && (s.item.querySelector("svg") || s.item);
      if (target) target.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
    };

    bar.addEventListener("touchstart", onStart, { passive: true });
    bar.addEventListener("touchmove", onMove, { passive: false });
    bar.addEventListener("touchend", onEnd, { passive: false });
    bar.addEventListener("touchcancel", onEnd, { passive: false });
    return () => {
      bar.removeEventListener("touchstart", onStart);
      bar.removeEventListener("touchmove", onMove);
      bar.removeEventListener("touchend", onEnd);
      bar.removeEventListener("touchcancel", onEnd);
    };
  });

  return { barRef, lens };
}
